import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, X, Wallet, IndianRupee, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Phase 22b — TransportAccountsFinalModal (stage: `transport_accounts_final`).
 *
 * Dual-approver (Sangita.K OR Amey.G). Captures the final payment info:
 *   • accounts_final_amount_debited (required free text)
 *   • accounts_final_remark (required free text)
 */
export default function TransportAccountsFinalModal({ open, onClose, onDone, requisition, actingAsBanner = null }) {
  const [amt, setAmt] = useState("");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const firstRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setAmt(""); setRemark(""); setError("");
    setTimeout(() => firstRef.current?.focus(), 20);
  }, [open, requisition?.ref_code]);

  if (!open || !requisition) return null;
  const trimmedAmt = amt.trim();
  const trimmedRemark = remark.trim();
  const canSubmit = !busy && trimmedAmt.length > 0 && trimmedRemark.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        `/requisitions/${requisition.ref_code}/action`,
        {
          action: "transport_accounts_final",
          remarks: trimmedRemark,
          accounts_final_amount_debited: trimmedAmt,
          accounts_final_remark: trimmedRemark,
        },
      );
      toast.success(`Accounts closed — ${requisition.ref_code}`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="transport-accounts-final-modal"
    >
      <div
        className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <Wallet className="text-[#7a1f2b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Accounts Final — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 flex items-start gap-2"
                 data-testid="super-admin-acting-as-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>You are acting as <strong>{actingAsBanner}</strong>.</div>
            </div>
          )}
          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#b48a45] px-3 py-2 rounded">
            Record the final amount debited and any closing remark. Submitting
            will complete and lock the requisition.
          </div>
          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">
              {error}
            </div>
          )}
          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Amount Debited <span className="text-[#b3261e]">*</span>
            </span>
            <div className="relative">
              <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e7770]" />
              <input
                ref={firstRef}
                type="text"
                value={amt}
                onChange={(e) => setAmt(e.target.value.slice(0, 200))}
                placeholder="e.g. ₹ 2,350"
                className="input-vgws pl-9"
                data-testid="accounts-final-amount-debited"
              />
            </div>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Accounts Final Remark <span className="text-[#b3261e]">*</span>
            </span>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value.slice(0, 1000))}
              rows={4}
              className="input-vgws min-h-[100px]"
              placeholder="Payment released to vendor, invoice ref…"
              data-testid="accounts-final-remark"
            />
          </label>
        </div>
        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="accounts-final-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm bg-[#2d6b6b] hover:bg-[#1c4a4a] border-[#1c4a4a] ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="accounts-final-confirm"
          >
            {busy ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={16} />}
            {busy ? "Submitting…" : "Mark Debited & Complete"}
          </button>
        </div>
      </div>
    </div>
  );
}
